/**
 * Rules section (inside `CapabilityDrawer`) — the per-rule successor to the
 * `## Rules` markdown bullets that used to live in `bodyMd`. Each rule is its
 * own record (`packages/db/src/repositories/product-map/rules.ts`): a human
 * edit or add makes that rule `source: 'user'`, which IS the rule's lock —
 * unlike areas/capabilities there is no per-field `lockedFields` list, since
 * a rule is a single statement. An agent-authored rule not yet touched by a
 * human shows an "Agent" badge, same visual language as
 * `CapabilityPersonasSection`'s Human/Agent badge.
 *
 * Follows `ChecklistPanel`'s add/edit/remove shape (inline `TextField` to
 * rename, trailing add row) rather than `InlineEditableText`, since that
 * component edits one persistent value in place — it has no "commit and
 * clear for the next one" mode a running list of new rules needs.
 *
 * Duplicate-statement (`PRODUCT_MAP_RULE_DUPLICATE`) and other save failures
 * surface inline via `mutation.error`, following `PurposeCard`'s pattern:
 * `mutateAsync` in a try/catch that stays in edit mode on failure so the
 * error has something to sit next to.
 *
 * @module features/product-map/components/CapabilityRulesSection
 */

import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Link } from '@tanstack/react-router';
import { Plus, Trash } from '@phosphor-icons/react';
import { Badge, Button, CollapsibleSection, EditableBadge, Flex, ListCard, Text, TextField } from '@chatsdet/ui';
import type { ProductMapStatus } from '@chatsdet/types';
import { STAGE_OPTIONS, STAGE_VARIANT } from '../stage';
import { useAddRule, useRemoveRule, useUpdateRule } from '../useProductMap';
import type { CapabilityRule } from '../types';

const RULE_COVERAGE_BADGE: Record<
  CapabilityRule['coverage'],
  { label: string; variant: 'success' | 'warning' | 'outline' }
> = {
  proven: { label: 'Proven', variant: 'success' },
  unproven: { label: 'Unproven', variant: 'warning' },
  gap: { label: 'No coverage', variant: 'outline' },
};

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

type Props = {
  capabilityId: string;
  rules: CapabilityRule[];
};

export function CapabilityRulesSection({ capabilityId, rules }: Props) {
  const addRule = useAddRule(capabilityId);
  const updateRule = useUpdateRule(capabilityId);
  const removeRule = useRemoveRule(capabilityId);

  const [newStatement, setNewStatement] = useState('');

  const handleAdd = async () => {
    const trimmed = newStatement.trim();
    if (!trimmed) return;
    try {
      await addRule.mutateAsync(trimmed);
      setNewStatement('');
    } catch {
      // addRule.error drives the inline error message below; keep the draft.
    }
  };

  return (
    <CollapsibleSection label="Rules" count={rules.length} defaultExpanded={rules.length > 0}>
      {rules.length === 0 ? (
        <Text size="sm" className="text-muted-foreground">
          No rules yet — add one below.
        </Text>
      ) : (
        <ListCard>
          {rules.map((rule) => (
            <RuleRow
              key={rule.id}
              rule={rule}
              onSave={(statement) => updateRule.mutateAsync({ id: rule.id, statement })}
              onStageChange={(status) => updateRule.mutateAsync({ id: rule.id, status })}
              onRemove={() => removeRule.mutate(rule.id)}
              isRemoving={removeRule.isPending && removeRule.variables === rule.id}
            />
          ))}
        </ListCard>
      )}

      <Flex direction="column" gap="xs" className="mt-2">
        <Flex gap="xs" align="center">
          <TextField
            aria-label="New rule"
            value={newStatement}
            onChange={setNewStatement}
            onKeyDown={(e: KeyboardEvent) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void handleAdd();
              }
            }}
            placeholder="Add a rule this capability must satisfy…"
            className="flex-1"
          />
          <Button
            variant="ghost"
            size="sm"
            onPress={() => void handleAdd()}
            isDisabled={!newStatement.trim() || addRule.isPending}
            iconLeft={<Plus size={14} />}
          >
            Add
          </Button>
        </Flex>
        {addRule.error && (
          <Text size="xs" className="text-destructive">
            {errorMessage(addRule.error, 'Failed to add rule')}
          </Text>
        )}
      </Flex>
    </CollapsibleSection>
  );
}

function RuleRow({
  rule,
  onSave,
  onStageChange,
  onRemove,
  isRemoving,
}: {
  rule: CapabilityRule;
  onSave: (statement: string) => Promise<unknown>;
  onStageChange: (status: ProductMapStatus) => Promise<unknown>;
  onRemove: () => void;
  isRemoving: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(rule.statement);
  const [error, setError] = useState<string | null>(null);
  // Escape sets `editing` false synchronously, which unmounts the TextField
  // and fires its `onBlur` in the same tick — without this guard that blur
  // ran `commit()` a second time right after Escape's cancel. Set before
  // `setEditing(false)` on the cancel path and checked (then cleared) at the
  // top of `commit`, so the blur-triggered call becomes a no-op.
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!editing) setDraft(rule.statement);
  }, [rule.statement, editing]);

  const cancel = () => {
    cancelledRef.current = true;
    setDraft(rule.statement);
    setError(null);
    setEditing(false);
  };

  const commit = async () => {
    if (cancelledRef.current) {
      cancelledRef.current = false;
      return;
    }
    const trimmed = draft.trim();
    if (!trimmed || trimmed === rule.statement) {
      setEditing(false);
      setDraft(rule.statement);
      return;
    }
    try {
      await onSave(trimmed);
      setError(null);
      setEditing(false);
    } catch (err) {
      setError(errorMessage(err, 'Failed to save rule'));
    }
  };

  return (
    <div className="group space-y-1 px-4 py-2.5">
      <Flex align="center" gap="sm">
        {rule.source === 'agent' && (
          <Badge size="sm" variant="outline" className="shrink-0">
            Agent
          </Badge>
        )}
        {editing ? (
          <TextField
            aria-label="Edit rule"
            value={draft}
            onChange={setDraft}
            onBlur={() => void commit()}
            onKeyDown={(e: KeyboardEvent) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void commit();
              }
              if (e.key === 'Escape') {
                cancel();
              }
            }}
            autoFocus
            className="flex-1"
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="min-w-0 flex-1 text-pretty text-left text-sm text-foreground"
          >
            {rule.statement}
          </button>
        )}
        <EditableBadge<ProductMapStatus>
          value={rule.status}
          options={STAGE_OPTIONS}
          onChange={(status) => {
            // The rule update's global toast is suppressed (statement errors
            // render inline), so stage-order (409) errors render here too.
            onStageChange(status).then(
              () => setError(null),
              (err: unknown) => setError(errorMessage(err, 'Failed to update rule stage'))
            );
          }}
          variant={STAGE_VARIANT[rule.status]}
          size="sm"
          aria-label={`Stage for rule: ${rule.statement}`}
        />
        <button
          type="button"
          onClick={onRemove}
          disabled={isRemoving}
          aria-label={`Remove rule: ${rule.statement}`}
          className="shrink-0 rounded-full p-0.5 text-muted-foreground opacity-0 transition-colors group-hover:opacity-100 hover:text-destructive focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
        >
          <Trash size={14} />
        </button>
      </Flex>
      <Flex direction="column" gap="xs">
        <div>
          <Badge size="sm" variant={RULE_COVERAGE_BADGE[rule.coverage].variant}>
            {RULE_COVERAGE_BADGE[rule.coverage].label}
          </Badge>
        </div>
        {rule.links.length > 0 && (
          <ul className="space-y-0.5">
            {rule.links.map((link) => (
              <li key={`${link.nodeId}:${link.assertionId ?? ''}`} className="text-xs text-muted-foreground">
                <Link
                  to="/flows/$workflowId"
                  params={{ workflowId: link.workflowId }}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-primary hover:underline"
                >
                  {link.workflowTitle} › {link.nodeTitle}
                </Link>
                {link.assertionText && <span className="text-pretty"> — {link.assertionText}</span>}
              </li>
            ))}
          </ul>
        )}
      </Flex>
      {error && (
        <Text size="xs" className="text-destructive">
          {error}
        </Text>
      )}
    </div>
  );
}
